// 3rd party components goes here
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

// application components goes here
import FavoriteArticles from "./FavoriteArticles";
import { getFavoriteNews } from "../actions";
import store, { RootState } from "../../../../store/index";
import { IArticlesItem } from "../../interfaces";
import { chunkArray } from "../../../../utils";
import ListPlaceholder from "../../WhatsHappeningNow/Components/ListPlaceholder";
import WhnPlaceholder from "../../WhatsHappeningNow/Components/WhnPlaceholder";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import EmptyTabInfo from "../../../Content/Shared/EmptyTabInfo";
import SelectedBottom from "modules/Common/Components/SelectedBottom";
import { removeSelectedIds } from "modules/Content/DragnDrop/actions";
import { PREVIEW_ARTICLE } from "constants/actionTypes";
import { getQueryCampaignId, getQueryEmailId } from "modules/EmailBrowser/utils";

// styles goes here
import styled from "styled-components";

type MyAccountsContainerProps = {
  isListView: boolean;
};

const FavoriteNewsContainer: React.FC<MyAccountsContainerProps> = (props: MyAccountsContainerProps) => {
  const { isListView } = props;

  const FavoriteNews: IArticlesItem[] = useSelector((state: RootState) => state.favoriteNews.data);
  const moreArticlesPresent: boolean = useSelector((state: RootState) => state.favoriteNews.moreArticlesPresent);
  const FavoriteNewsLoading: boolean = useSelector((state: RootState) => state.favoriteNews.loading);
  const FavoriteNewsPage: number = useSelector((state: RootState) => state.favoriteNews.page);
  const selectedIds = useSelector((state: RootState) => state.dragDrop.selectedIds);
  const [firstTimeLoad, setFirstTimeLoad] = useState(false);
  const isCampaign = window.location.pathname.includes("campaigns");
  const isEmail = window.location.pathname.includes("editionedit");

  const getPagination = () => {
    let offset = FavoriteNewsPage > 0 ? FavoriteNews.length : 0;
    const limit = moreArticlesPresent ? FavoriteNews.length + 22 : FavoriteNews.length;
    return { result_offset: offset, result_limit: limit, emailId: getQueryEmailId() };
  };

  const loadMore = () => {
    if (!FavoriteNewsLoading) getFavoriteNews(getPagination());
  };

  useEffect(() => {
    if (FavoriteNews.length === 0 && !FavoriteNewsLoading) {
      getFavoriteNews(getPagination());
    }
    store.dispatch({
      type: PREVIEW_ARTICLE.RESET_SEARCH_TERMS,
    });
  }, []);

  useEffect(() => {
    if (FavoriteNews.length < 22 && firstTimeLoad) {
      if (moreArticlesPresent) getFavoriteNews(getPagination());
    }
    setFirstTimeLoad(true);
  }, [FavoriteNews.length]);

  if (FavoriteNews.length === 0 && !FavoriteNewsLoading) {
    return (
      <div className="padding-top-xl padding-bottom-lg">
        <EmptyTabInfo
          description="As you’re exploring content in Workbench, you can easily favourite content by hitting the favourite icon. When you do, they’ll show up here."
          btntext={isCampaign ? "Back to SmartSourced Content" : "Back to What's Happening Now"}
          url={
            isCampaign
              ? isEmail
                ? `/campaigns/editionedit/source?campaign_id=${getQueryCampaignId()}&email_id=${getQueryEmailId()}`
                : `/campaigns/content/source?campaign_id=${getQueryCampaignId()}`
              : "/content/explorer"
          }
        />
      </div>
    );
  }

  return (
    <MyAccountsStyle className="inner-favorite">
      <InfiniteLoader
        dataLength={FavoriteNews.length}
        next={loadMore}
        hasMore={true}
        loader={
          isListView ? (
            <div className="padding-top-md">
              <ListPlaceholder loading={FavoriteNewsLoading} />
            </div>
          ) : (
            <div className="padding-top-md">
              <WhnPlaceholder loading={FavoriteNewsLoading} />
            </div>
          )
        }
      >
        <div className="fav-padding">
          {chunkArray(FavoriteNews, 22).map((subArtilces, index) => (
            <FavoriteArticles articles={subArtilces} key={index} isListView={isListView} />
          ))}
        </div>
      </InfiniteLoader>
      <SelectedBottom
        hideAction={true}
        selectedCount={selectedIds?.length}
        selectedMessage={` ${selectedIds.length === 1 ? "article" : "articles"} selected`}
        onClearSelection={removeSelectedIds}
      />
    </MyAccountsStyle>
  );
};

export default FavoriteNewsContainer;

const MyAccountsStyle = styled.div`
  display: inline-block;
  h2.accounts-title {
    color: #67696a;
    font-size: 22px;
    font-weight: 500;
    font-family: "robotomedium";
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
    }
    span {
      font-family: "robotolight_italic";
      padding-left: 25px;
      font-size: 18px;
      font-weight: 400;
    }
  }
  div[role="DraggableBox"] {
    height: 100%;
    div[role="Box"] {
      height: 100%;
    }
  }
  .list-style {
    div[role="DraggableBox"] {
      height: auto;
      div[role="Box"] {
        height: auto;
      }
    }
  }
  .small-three {
    height: 100%;
    flex-direction: column;
    display: flex;
    justify-content: start;
    align-items: center;
    flex-wrap: wrap;
    &:not(.small-two-only) {
      div[role="DraggableBox"]:nth-of-type(2) {
        margin-top: 15px;
      }
    }
    div[role="DraggableBox"] {
      height: auto;
      div[role="Box"] {
        height: auto;
      }
    }
    &.small-two-only {
      justify-content: space-between;
    }
  }
  .account-row {
    display: block;
    float: left;
    min-width: 100%;
    height: 200px;
    position: relative;
    > div {
      width: calc(100% + 90px);
      float: left;
      position: absolute;
      z-index: 0;
      overflow-x: scroll;
      overflow-y: hidden;
      // -ms-overflow-style: none; /* IE and Edge */
      // scrollbar-width: none; /* Firefox */
      // &::-webkit-scrollbar {
      //   display: none;
      // }
      ::-webkit-scrollbar {
        width: 40px;
        height: 5px;
        border-radius: 2px;
      }
      /* Track */
      ::-webkit-scrollbar-track {
        background: transparent;
        margin-top: 10px;
        margin-bottom: 15px;
      }

      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: #001760;
      }

      /* Handle on hover */
      ::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
    }
  }
`;
